import type { ProgramsFilterSelections } from '@makemydeal/dr-dash-types';

// Filter state containing selected values for each filter category
export type ProgramFilters = ProgramsFilterSelections;

// Individual filter option for checkbox display
export interface FilterOption {
    value: string;
    label: string;
    group?: string;
}

export type FilterOptionsByKey = Record<keyof ProgramFilters, FilterOption[]>;

// Applied filter tag for chip display
export interface AppliedFilterTag {
    filterKey: keyof ProgramFilters;
    value: string;
    label: string;
}

// Props for the ProgramsFiltersSlideOut component
export interface ProgramsFiltersSlideOutProps {
    isOpen: boolean;
    onClose: () => void;
    hasNoPrograms: boolean;
    filterOptions: FilterOptionsByKey;
    filters: ProgramFilters;
    onFiltersChange: (filters: ProgramFilters) => void;
    onApplyFilters: () => void;
    offerType: string;
    isLoading?: boolean;
}

// Configuration for each filter section
export interface FilterConfig {
    filterKey: keyof ProgramFilters;
    label: string;
    testIdPrefix: string;
    leaseOnly: boolean;
    useGridLayout?: boolean;
    useRadioButtons?: boolean;
    requiresPIScreenEnhancements?: boolean;
    hideWhenReadOnly?: boolean;
    requiresWidePanel?: boolean;
    useEnhancedTypography?: boolean;
}

export const FILTER_CONFIGS: FilterConfig[] = [
    {
        filterKey: 'lenders',
        label: 'Lender',
        testIdPrefix: 'lender',
        leaseOnly: false,
        useGridLayout: false
    },
    {
        filterKey: 'terms',
        label: 'Term',
        testIdPrefix: 'term',
        leaseOnly: false,
        useGridLayout: true
    },
    {
        filterKey: 'milesPerYear',
        label: 'Miles/yr',
        testIdPrefix: 'miles',
        leaseOnly: true,
        useGridLayout: false
    },
    {
        filterKey: 'ratePrograms',
        label: 'Rate Program',
        testIdPrefix: 'rate-program',
        leaseOnly: true,
        useGridLayout: false
    },
    {
        filterKey: 'creditTiers',
        label: 'Credit Score/Tier',
        testIdPrefix: 'credit-tier',
        leaseOnly: false,
        useGridLayout: false,
        useRadioButtons: true
    },
    {
        filterKey: 'lenderTiers',
        label: 'Lender Tier List',
        testIdPrefix: 'lender-tier',
        leaseOnly: false,
        useGridLayout: false,
        requiresPIScreenEnhancements: true,
        hideWhenReadOnly: true,
        requiresWidePanel: true,
        useEnhancedTypography: true
    }
];

export const DEFAULT_FILTERS: ProgramFilters = {
    lenders: [],
    terms: [],
    milesPerYear: [],
    ratePrograms: [],
    creditTiers: [],
    lenderTiers: []
};

export const FILTERS_PANEL_WIDTH = '400px';
export const FILTERS_WIDE_PANEL_WIDTH = 'min(700px, 100vw)';

export const NO_RESULTS_MESSAGE = 'Sorry, there are no results available. Please try a different selection.';
export const MAX_VISIBLE_FILTER_CHIPS = 3;

export const getVisibleFilterConfigs = (showAllFilters: boolean, isPIScreenEnhancementsEnabled: boolean): FilterConfig[] => {
    return FILTER_CONFIGS.filter((config) => {
        if (config.requiresPIScreenEnhancements && !isPIScreenEnhancementsEnabled) return false;
        if (isPIScreenEnhancementsEnabled && config.filterKey === 'ratePrograms') return true;
        return !config.leaseOnly || showAllFilters;
    });
};
